import { useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import type { BattleInfo } from "../shared-sortaballs/PuzzleHeader"

import { Sortaballs8ColorsApp } from "../sortaballs-8-colors/Sortaballs8ColorsApp"
import { sortaballs8ColorsLevelStore } from "../sortaballs-8-colors/level-store"
import { USER_NAME } from "../config/constants"
import { fireErrorFeedbackCanvas } from "../modules/feedback-canvas"
import { Button } from "../shared-components/button"
import { toast } from "../shared-components/ui/toast"
import { sortaballs10ColorsLevelStore } from "../sortaballs-10-colors/level-store"
import { Sortaballs10ColorsApp } from "../sortaballs-10-colors/Sortaballs10ColorsApp"
import {
  acceptChallenge,
  type ChallengeData,
  type ChallengeResult,
  createChallenge,
  getAvailableChallengesCount,
  getMyPendingCount,
} from "./api"
import { formatCountdown } from "./format-countdown"
import { HistoryView, LeaderboardView } from "./query-views"
import { useBattle } from "./use-battle"
import { useCreateBattle } from "./use-create-battle"
import "./penalty-flash.css"

// Forward the opt-in `?skip-flight=1` query (Board collapses flight animation
// for fast e2e replays) when entering a battle route, so the embedded game
// inherits it without changing behavior when the param is absent.
const battleSearch = (): string=> {
  const params = new URLSearchParams(window.location.search)
  return params.get("skip-flight") === "1" ? "?skip-flight=1" : ""
}

type RequestChallenge = ()=> Promise<ChallengeResult>

export function ChallengesApp() {
  const navigate = useNavigate()
  const { pathname, state } = useLocation()
  const params = useParams()

  // A battle page is valid only when reached from the home page, which passes
  // `happy` via router state (never the URL — so a reload or a direct visit
  // has no such state and is "broken", bouncing back home).
  // SAFETY: router state is null or { happy?: boolean } from Navigate
  const happy = (state as null | { happy?: boolean })?.happy === true

  const acceptBattle = useBattle(happy)
  const createBattle = useCreateBattle(happy)
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState<"history" | "home" | "leaderboard">("home")
  const [pendingCount, setPendingCount] = useState<null | number>(null)
  const [myEasyPendingCount, setMyEasyPendingCount] = useState<null | number>(null)
  const [myHardPendingCount, setMyHardPendingCount] = useState<null | number>(null)
  const isChildRoute =
    pathname.startsWith("/create-challenge") ||
    pathname.startsWith("/accept-challenge")
  const battleId = acceptBattle.challenge?.id ?? createBattle.challenge?.id
  const idMatches = params.id === undefined || String(battleId) === params.id
  const broken = isChildRoute && (!happy || !idMatches)

  useEffect(()=> {
    if (broken) {
      navigate("/sortaballs-challenges", { replace: true })
    }
  }, [broken, navigate])

  const startAccept = (request: RequestChallenge): void=> {
    if (loading) return
    setLoading(true)
    request()
      .then((result: ChallengeResult)=> {
        if (result.challenge) {
          // Point the embedded game's level store at the challenge's puzzle
          // BEFORE the game mounts, so the opening hint-move animation plays
          // on the right board (rendering first would seed the store after
          // the pre-roll has already started on the wrong puzzle).
          seedLevelForChallenge(result.challenge)
          acceptBattle.start(result.challenge)
          if (result.fromMock) {
            toast.info("Server unreachable — playing mock challenge")
          }
          navigate(
            `/accept-challenge/${result.challenge.id}${battleSearch()}`,
            {
              state: { happy: true },
            },
          )
        } else {
          toast.info("No challenge found — take or accept one")
        }
      })
      .finally(()=> {
        setLoading(false)
      })
  }

  const startCreate = (hardMode: boolean): void=> {
    if (loading) return
    setLoading(true)
    createChallenge(hardMode)
      .then((result: ChallengeResult)=> {
        if (result.challenge) {
          createBattle.start(result.challenge)
          if (result.fromMock) {
            toast.info("Server unreachable — creating mock challenge")
          }
          navigate(
            `/create-challenge/${result.challenge.id}${battleSearch()}`,
            {
              state: { happy: true },
            },
          )
        } else {
          toast.info("No challenge found — take or accept one")
        }
      })
      .finally(()=> {
        setLoading(false)
      })
  }

  // Point the embedded game's level store at the challenge's puzzle (its
  // `index`). Deterministic per challenge, so the game board matches it from
  // the first render.
  const seedLevelForChallenge = (challenge: ChallengeData): void=> {
    if (challenge.hard_mode) {
      sortaballs10ColorsLevelStore.setLevel(challenge.index)
    } else {
      sortaballs8ColorsLevelStore.setLevel(challenge.index)
    }
  }

  // Seed the right game's level store so it renders the challenge puzzle. Only
  // while genuinely in a battle (happy) — never on the landing page. (Backup
  // for battles restored from session storage; the click path seeds earlier.)
  useEffect(()=> {
    if (!happy) return
    const active = acceptBattle.challenge ?? createBattle.challenge
    if (active) {
      seedLevelForChallenge(active)
    }
  }, [happy, acceptBattle.challenge, createBattle.challenge])

  useEffect(()=> {
    if (acceptBattle.solved && !acceptBattle.lost) {
      toast.success("You won the challenge!")
    }
  }, [acceptBattle.lost, acceptBattle.solved])

  // Countdown hit zero: the player lost. Error confetti + a haptic buzz,
  // independent of the animation flag (mirroring the success celebration).
  useEffect(()=> {
    if (acceptBattle.lost && !acceptBattle.solved) {
      fireErrorFeedbackCanvas(true)
    }
  }, [acceptBattle.lost, acceptBattle.solved])

  // Live count of challenges this user can still accept, shown on the home
  // "Accept challenge" button. Refetched on return to home and whenever a
  // battle starts or ends — accepting consumes a challenge, so the count must
  // drop immediately rather than waiting for a `view` change (the accept flow
  // navigates by router, never touching `view`).
  useEffect(()=> {
    if (view !== "home") return
    const name = localStorage.getItem(USER_NAME) ?? ""
    getAvailableChallengesCount(name)
      .then(setPendingCount)
      .catch(()=> setPendingCount(null))
  }, [view, acceptBattle.challenge, createBattle.challenge])

  // Live count of challenges this user created but no opponent accepted yet.
  useEffect(()=> {
    if (view !== "home") return
    const name = localStorage.getItem(USER_NAME) ?? ""
    getMyPendingCount(name, false)
      .then(setMyEasyPendingCount)
      .catch(()=> setMyEasyPendingCount(null))
    getMyPendingCount(name, true)
      .then(setMyHardPendingCount)
      .catch(()=> setMyHardPendingCount(null))
  }, [view, acceptBattle.challenge, createBattle.challenge])

  if (loading) {
    return <div>Loading…</div>
  }
  if (happy && acceptBattle.challenge) {
    const battle: BattleInfo = {
      countdownText:
        acceptBattle.challenge.time === null
          ? "waiting…"
          : formatCountdown(acceptBattle.remainingSeconds),
      countdownUrgent: acceptBattle.remainingSeconds < 10,
      exitLabel: acceptBattle.lost ? "stop" : "Back",
      onConcede: acceptBattle.concede,
      onExit: acceptBattle.stop,
      onTogglePause: acceptBattle.togglePause,
      opponent: {
        hints: acceptBattle.challenge.hints_used,
        time: acceptBattle.challenge.time,
      },
      paused: acceptBattle.paused,
      penaltyCount: acceptBattle.penaltyCount,
      // Accept: each penalized undo removes 1s from the countdown.
      penaltyLabel: "-1",
    }
    return (
      <div className="flex w-full flex-col items-center">
        <div
          className="flex w-full flex-col items-center"
          ref={acceptBattle.containerRef}
        >
          {acceptBattle.challenge.hard_mode ? (
            <Sortaballs10ColorsApp
              advancedBy={acceptBattle.challenge.hints_used}
              battle={battle}
              hideHints
            />
          ) : (
            <Sortaballs8ColorsApp
              advancedBy={acceptBattle.challenge.hints_used}
              battle={battle}
              hideHints
            />
          )}
        </div>
      </div>
    )
  }
  if (happy && createBattle.challenge) {
    const battle: BattleInfo = {
      countdownText: formatCountdown(createBattle.displaySeconds),
      exitLabel: "Back",
      onExit: createBattle.stop,
      onTogglePause: createBattle.togglePause,
      paused: createBattle.paused,
      penaltyCount: createBattle.penaltyCount,
      // Create: each penalized undo adds 1s to the measured time.
      penaltyLabel: "+1",
    }
    return (
      <div className="flex w-full flex-col items-center">
        <div
          className="flex w-full flex-col items-center"
          ref={createBattle.containerRef}
        >
          {createBattle.challenge.hard_mode ? (
            <Sortaballs10ColorsApp battle={battle} />
          ) : (
            <Sortaballs8ColorsApp battle={battle} />
          )}
        </div>
      </div>
    )
  }
  if (view === "leaderboard") {
    return <LeaderboardView onBack={()=> setView("home")} />
  }
  if (view === "history") {
    return <HistoryView onBack={()=> setView("home")} />
  }
  return (
    <div className="mx-auto flex max-w-md flex-col gap-4 px-4 pt-12">
      <h1 className="text-center text-2xl font-bold">Sortaballs Challenges</h1>
      <Button onClick={()=> startCreate(false)} size="sm" variant="outline">
        {`Create challenge${
          myEasyPendingCount === null
            ? ""
            : myEasyPendingCount === 0
              ? ""
              : ` (${myEasyPendingCount})`
        }`}
      </Button>
      <Button onClick={()=> startCreate(true)} size="sm" variant="outline">
        {`Create hard challenge${
          myHardPendingCount === null
            ? ""
            : myHardPendingCount === 0
              ? ""
              : ` (${myHardPendingCount})`
        }`}
      </Button>
      <Button
        disabled={pendingCount === 0}
        onClick={()=> startAccept(acceptChallenge)}
        size="sm"
        variant="outline"
      >
        {`Accept challenge${
          pendingCount === null
            ? ""
            : pendingCount === 0
              ? " — none available"
              : ` (${pendingCount})`
        }`}
      </Button>
      <Button onClick={()=> setView("history")} size="sm" variant="outline">
        History
      </Button>
      <Button
        onClick={()=> setView("leaderboard")}
        size="sm"
        variant="outline"
      >
        Leaderboard
      </Button>
    </div>
  )
}
